import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Banknote, PlayCircle, Pause, RefreshCw, Eye, Filter, Calendar, CheckCircle2, AlertTriangle, Activity, History } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCHF, formatDateTime } from "@/lib/format";

const STATUS_TONE = {
  external_remuneration: "bg-slate-50 text-slate-700",
  pending_payment: "bg-slate-50 text-slate-700",
  pending_connect: "bg-amber-50 text-amber-700",
  ready: "bg-blue-50 text-blue-700",
  under_review: "bg-orange-50 text-orange-700",
  blocked: "bg-red-50 text-red-700",
  scheduled: "bg-violet-50 text-violet-700",
  transferred: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
  cancelled: "bg-slate-50 text-slate-500",
};

const STATUS_LABEL = {
  external_remuneration: "Fuori Stripe · da verificare",
  pending_payment: "In attesa pagamento",
  pending_connect: "Senza Stripe Connect",
  ready: "Pronto",
  under_review: "In revisione",
  blocked: "Bloccato",
  scheduled: "In esecuzione",
  transferred: "Trasferito",
  failed: "Fallito",
  cancelled: "Annullato",
};

const STATUS_OPTIONS = Object.entries(STATUS_LABEL);

export default function AdminPayouts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", driver_id: "", days: 90 });
  const [busy, setBusy] = useState(false);
  const [batchPreview, setBatchPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [scheduler, setScheduler] = useState(null);
  const [batches, setBatches] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { days: filter.days };
      if (filter.status) params.status = filter.status;
      if (filter.driver_id) params.driver_id = filter.driver_id;
      const [resPayouts, resSched, resBatches] = await Promise.all([
        api.get("/admin/payouts", { params }),
        api.get("/admin/payouts/scheduler/status").catch(() => ({ data: null })),
        api.get("/admin/payouts/batches?limit=10").catch(() => ({ data: { items: [] } })),
      ]);
      setData(resPayouts.data);
      setScheduler(resSched.data);
      setBatches(resBatches.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status, filter.driver_id, filter.days]);

  const block = async (id) => {
    const reason = window.prompt("Motivo blocco (opzionale):") || "";
    setBusy(true);
    try { await api.post(`/admin/payouts/${id}/block`, { reason }); toast.success("Payout bloccato"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const unblock = async (id) => {
    setBusy(true);
    try { await api.post(`/admin/payouts/${id}/unblock`); toast.success("Payout sbloccato"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const underReview = async (id) => {
    const reason = window.prompt("Motivo revisione (opzionale):") || "";
    setBusy(true);
    try { await api.post(`/admin/payouts/${id}/under-review`, { reason }); toast.message("Payout in revisione"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const retry = async (id) => {
    if (data?.payouts_authorized !== true) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/payouts/${id}/retry`); toast.success(`Retry: ${data.status}`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const previewBatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payouts/batch/preview");
      setBatchPreview(data);
      setShowPreview(true);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const runBatch = async () => {
    if (data?.payouts_authorized !== true) return;
    if (!window.confirm(`Eseguire il batch payout? ${batchPreview?.count || 0} payout · ${formatCHF(batchPreview?.total_amount || 0)}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payouts/batch/run");
      toast.success(`Batch: ${data.succeeded} ok · ${data.failed} falliti · ${formatCHF(data.total_transferred || 0)} trasferiti`);
      setShowPreview(false);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const totals = data?.totals || {};
  const nextBatch = data?.next_batch || {};
  const items = data?.items || [];
  const payoutsAuthorized = data?.payouts_authorized === true;

  const statTiles = useMemo(() => ([
    { key: payoutsAuthorized ? "ready" : "external_remuneration", label: payoutsAuthorized ? "Pronti per il batch" : "Fuori Stripe · da verificare", value: payoutsAuthorized ? totals.ready : totals.external_remuneration, accent: "text-blue-700 bg-blue-50" },
    { key: "transferred", label: "Trasferiti", value: totals.transferred, accent: "text-emerald-700 bg-emerald-50" },
    { key: "failed", label: "Falliti", value: totals.failed, accent: "text-red-700 bg-red-50" },
    { key: "blocked", label: "Bloccati / In revisione", value: { count: (totals.blocked?.count || 0) + (totals.under_review?.count || 0), amount: (totals.blocked?.amount || 0) + (totals.under_review?.amount || 0) }, accent: "text-orange-700 bg-orange-50" },
  ]), [totals, payoutsAuthorized]);

  return (
    <div className="space-y-6" data-testid="admin-payouts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline">Pagamenti driver</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">{payoutsAuthorized ? "Payout Stripe Connect" : "Registrazioni compensi"}</h1>
          <p className="text-sm text-dapnow-muted mt-2">Gli importi registrati sono calcoli da verificare; non definiscono il compenso concordato e non attestano un pagamento.</p>
        </div>
        {payoutsAuthorized && <div className="flex gap-2">
          <button onClick={previewBatch} data-testid="batch-preview-btn" disabled={busy} className="btn-ghost inline-flex items-center gap-1.5">
            <Eye className="h-4 w-4" /> Anteprima batch
          </button>
        </div>}
      </div>

      {/* Tiles */}
      <section className="grid sm:grid-cols-4 gap-3">
        {statTiles.map((t) => (
          <div key={t.key} className="card-soft p-5" data-testid={`payout-stat-${t.key}`}>
            <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${t.accent}`}>
              <Banknote className="h-4 w-4" />
            </span>
            <p className="overline mt-3">{t.label}</p>
            <p className="font-display text-2xl font-extrabold tracking-tight mt-1">{formatCHF(t.value?.amount || 0)}</p>
            <p className="text-xs text-dapnow-muted mt-1">{t.value?.count || 0} payout</p>
          </div>
        ))}
      </section>

      {/* Scheduler status */}
      {payoutsAuthorized && <SchedulerCard scheduler={scheduler} />}

      {!payoutsAuthorized && (
        <p className="card-soft p-5 text-sm" data-testid="internal-fleet-payout-notice">
          I driver interni sono remunerati da DapNow fuori da Stripe. I trasferimenti automatici sono disattivati; le nuove registrazioni sono da verificare con il referente, mentre lo storico è conservato.
        </p>
      )}

      {/* Next batch banner */}
      {payoutsAuthorized && (
      <div className="card-soft border border-blue-200 bg-blue-50 p-5 flex items-center gap-4 flex-wrap">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700"><Calendar className="h-5 w-5" /></span>
        <div className="flex-1 min-w-[200px]">
          <p className="font-semibold tracking-tight">Prossimo batch lunedì</p>
          <p className="text-xs text-blue-900/70">{formatDateTime(nextBatch.scheduled_for)} · <strong>{nextBatch.count}</strong> payout pronti · {formatCHF(nextBatch.amount || 0)}</p>
        </div>
        <button onClick={previewBatch} disabled={busy} className="btn-ghost inline-flex items-center gap-1.5" data-testid="batch-preview-cta">
          <Eye className="h-4 w-4" /> Anteprima
        </button>
        <button onClick={async () => { await previewBatch(); }} disabled={busy} data-testid="batch-run-cta" className="btn-primary inline-flex items-center gap-1.5">
          <PlayCircle className="h-4 w-4" /> Esegui batch
        </button>
      </div>
      )}

      {/* Filters */}
      <div className="card-soft p-4 flex items-center gap-3 flex-wrap" data-testid="payouts-filters">
        <Filter className="h-4 w-4 text-dapnow-muted" />
        <select value={filter.status} onChange={(e) => setFilter((s) => ({ ...s, status: e.target.value }))} className="input-tall" data-testid="payouts-status-filter">
          <option value="">Tutti gli stati</option>
          {STATUS_OPTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <input
          value={filter.driver_id}
          onChange={(e) => setFilter((s) => ({ ...s, driver_id: e.target.value }))}
          placeholder="Filtro driver_id"
          className="input-tall"
          data-testid="payouts-driver-filter"
        />
        <select value={filter.days} onChange={(e) => setFilter((s) => ({ ...s, days: Number(e.target.value) }))} className="input-tall" data-testid="payouts-window-filter">
          <option value={30}>Ultimi 30 giorni</option>
          <option value={90}>Ultimi 90 giorni</option>
          <option value={180}>Ultimi 180 giorni</option>
          <option value={365}>Ultimo anno</option>
        </select>
        <button onClick={load} className="btn-ghost inline-flex items-center gap-1.5"><RefreshCw className="h-4 w-4" /> Aggiorna</button>
      </div>

      {/* Table */}
      <section className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: "1100px" }} data-testid="payouts-table">
            <thead className="bg-dapnow-bg/40 text-dapnow-muted">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Driver</th>
                <th className="px-4 py-3 text-left font-semibold">Ordine</th>
                <th className="px-4 py-3 text-right font-semibold">Lordo</th>
                <th className="px-4 py-3 text-right font-semibold">Importo registrato</th>
                <th className="px-4 py-3 text-right font-semibold">Fee</th>
                <th className="px-4 py-3 text-left font-semibold">Stato</th>
                <th className="px-4 py-3 text-left font-semibold">Stripe</th>
                <th className="px-4 py-3 text-left font-semibold">Creato</th>
                <th className="px-4 py-3 text-right font-semibold">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={9} className="py-10 text-center text-dapnow-muted">Caricamento…</td></tr>)}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="py-10 text-center text-dapnow-muted">Nessun payout in questa finestra.</td></tr>
              )}
              {items.map((p) => (
                <tr key={p.id} className="border-t border-dapnow-line" data-testid={`payout-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold">{p.driver_name || "—"}</p>
                    <p className="text-xs text-dapnow-muted">{p.driver_email}</p>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{p.order_id?.slice(0, 8)}…</td>
                  <td className="px-4 py-3 text-right">{formatCHF(p.gross_amount)}</td>
                  <td className="px-4 py-3 text-right font-bold">{formatCHF(p.driver_payout_amount)}</td>
                  <td className="px-4 py-3 text-right text-dapnow-muted">{formatCHF(p.platform_fee)}</td>
                  <td className="px-4 py-3">
                    <span className={`pill ${STATUS_TONE[p.status] || "bg-slate-50 text-slate-700"}`}>
                      {STATUS_LABEL[p.status] || p.status}
                    </span>
                    {p.simulated && <span className="ml-1 pill bg-blue-50 text-blue-700 text-[10px]">SIM</span>}
                    {p.failure_reason && <p className="text-[11px] text-red-700 mt-1 truncate max-w-[200px]" title={p.failure_reason}>{p.failure_reason}</p>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <p>{p.remuneration_method === "external" ? "Non richiesto" : p.driver_stripe_status || "not_started"}</p>
                    {p.stripe_transfer_id && <p className="font-mono text-[11px] text-dapnow-muted">{p.stripe_transfer_id.slice(0, 14)}…</p>}
                  </td>
                  <td className="px-4 py-3 text-xs text-dapnow-muted">{formatDateTime(p.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1.5 flex-wrap">
                      {payoutsAuthorized && p.remuneration_method !== "external" && (p.status === "ready" || p.status === "scheduled") && (
                        <>
                          <button onClick={() => block(p.id)} disabled={busy} className="btn-ghost text-xs px-2 py-1" data-testid={`payout-block-${p.id}`}>Blocca</button>
                          <button onClick={() => underReview(p.id)} disabled={busy} className="btn-ghost text-xs px-2 py-1" data-testid={`payout-review-${p.id}`}>Revisione</button>
                        </>
                      )}
                      {payoutsAuthorized && p.remuneration_method !== "external" && (p.status === "blocked" || p.status === "under_review") && (
                        <button onClick={() => unblock(p.id)} disabled={busy} className="btn-ghost text-xs px-2 py-1" data-testid={`payout-unblock-${p.id}`}>Sblocca</button>
                      )}
                      {payoutsAuthorized && p.remuneration_method !== "external" && (p.status === "failed" || p.status === "ready" || p.status === "scheduled") && (
                        <button onClick={() => retry(p.id)} disabled={busy} className="btn-primary text-xs px-2 py-1" data-testid={`payout-retry-${p.id}`}>
                          {p.status === "failed" ? "Retry" : "Esegui ora"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Batch history */}
      <BatchHistoryCard batches={batches} />

      {/* Batch preview modal */}
      {showPreview && batchPreview && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="batch-preview-modal" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-dapnow-line flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-xl font-bold tracking-tight">Anteprima batch lunedì</h3>
                <p className="text-sm text-dapnow-muted">{batchPreview.count} payout · {formatCHF(batchPreview.total_amount)} totali</p>
              </div>
              <button onClick={() => setShowPreview(false)} className="btn-ghost">Chiudi</button>
            </div>
            <div className="p-6 space-y-2 text-sm">
              {batchPreview.items.length === 0 && <p className="text-dapnow-muted">Nessun payout pronto.</p>}
              {batchPreview.items.map((p) => (
                <div key={p.id} className="flex items-center justify-between border-b border-dapnow-line pb-2 last:border-0">
                  <div>
                    <p className="font-semibold">{p.driver_name}</p>
                    <p className="text-xs text-dapnow-muted">Ordine {p.order_id?.slice(0, 8)}… · {p.delivery_type}</p>
                  </div>
                  <p className="font-bold">{formatCHF(p.driver_payout_amount)}</p>
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-dapnow-line flex justify-end gap-2">
              <button onClick={() => setShowPreview(false)} className="btn-ghost">Annulla</button>
              <button onClick={runBatch} disabled={busy || !payoutsAuthorized || batchPreview.count === 0} data-testid="batch-confirm-btn" className="btn-primary inline-flex items-center gap-1.5">
                <PlayCircle className="h-4 w-4" /> {busy ? "In corso…" : "Esegui batch"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SchedulerCard({ scheduler }) {
  if (!scheduler) return null;
  const stateMap = {
    active: { label: "Attivo", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
    disabled: { label: "Disabilitato", cls: "bg-slate-50 text-slate-700 border-slate-200", dot: "bg-slate-400" },
    error: { label: "Errore", cls: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  };
  const meta = stateMap[scheduler.state] || stateMap.disabled;
  return (
    <section className={`card-soft p-5 border ${meta.cls}`} data-testid="scheduler-status-card">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/60 text-current">
          <Activity className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-[220px]">
          <p className="overline">Scheduler payout settimanale</p>
          <p className="font-display text-lg font-bold tracking-tight flex items-center gap-2 mt-0.5">
            <span className={`inline-block h-2 w-2 rounded-full ${meta.dot}`} /> {meta.label}
          </p>
          <p className="text-xs text-dapnow-muted mt-1">
            Cron: <code className="font-mono">{scheduler.cron}</code> · prossima esecuzione: <strong>{scheduler.next_run_at ? formatDateTime(scheduler.next_run_at) : "—"}</strong>
          </p>
          {scheduler.state === "error" && scheduler.last_error && (
            <p className="text-xs text-red-700 mt-1">Errore: {scheduler.last_error}</p>
          )}
        </div>
        <div className="text-xs text-dapnow-muted text-right">
          <p>Ultima esecuzione</p>
          <p className="font-semibold text-dapnow-ink">{scheduler.last_run_at ? formatDateTime(scheduler.last_run_at) : "Mai eseguito"}</p>
          {scheduler.last_batch && (
            <p className="mt-0.5">{scheduler.last_batch.processed} payout · {formatCHF(scheduler.last_batch.total_transferred || 0)}</p>
          )}
        </div>
      </div>
    </section>
  );
}

function BatchHistoryCard({ batches }) {
  return (
    <section className="card-soft p-5" data-testid="batch-history-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
          <History className="h-5 w-5 text-dapnow-blue" /> Storico batch payout
        </h2>
        <span className="text-xs text-dapnow-muted">Ultimi {batches.length}</span>
      </div>
      {batches.length === 0 ? (
        <p className="text-sm text-dapnow-muted py-4">Nessun batch eseguito finora.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-dapnow-muted">
              <tr>
                <th className="text-left font-semibold py-2">Data</th>
                <th className="text-left font-semibold py-2">Origine</th>
                <th className="text-right font-semibold py-2">Processati</th>
                <th className="text-right font-semibold py-2">Successi</th>
                <th className="text-right font-semibold py-2">Falliti</th>
                <th className="text-right font-semibold py-2">CHF trasferiti</th>
                <th className="text-right font-semibold py-2">Bloccati / Review</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className="border-t border-dapnow-line" data-testid={`batch-row-${b.id}`}>
                  <td className="py-2">{formatDateTime(b.started_at)}</td>
                  <td className="py-2">
                    <span className={`pill text-[11px] ${b.triggered_by === "scheduler" ? "bg-violet-50 text-violet-700" : "bg-blue-50 text-blue-700"}`}>
                      {b.triggered_by === "scheduler" ? "Auto" : "Manuale"}
                    </span>
                  </td>
                  <td className="py-2 text-right">{b.processed}</td>
                  <td className="py-2 text-right text-emerald-700 font-semibold">{b.succeeded}</td>
                  <td className="py-2 text-right text-red-700 font-semibold">{b.failed}</td>
                  <td className="py-2 text-right font-bold">{formatCHF(b.total_transferred || 0)}</td>
                  <td className="py-2 text-right text-xs text-dapnow-muted">{b.blocked_count_snapshot || 0} / {b.under_review_count_snapshot || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
